// Datumszelle für die Aufgabentabelle (Komfort- und Kompakt-Zeile).
//
// WARUM DIESE DATEI EXISTIERT
// Ein leeres `<input type="date">` ist in einer dichten Tabelle das lauteste
// Element der ganzen Zeile: der Browser malt seinen eigenen Platzhalter
// („tt.mm.jjjj") UND sein eigenes Kalendersymbol. Bei zehn Aufgaben mit je
// Start- und Enddatum sind das zwanzig Platzhalter und zwanzig Fremdsymbole,
// die alle „nichts" anzeigen — und das Symbol stammt aus einem völlig anderen
// Formvokabular als die Lucide-Icons ringsum.
//
// Die Zelle zeigt darum leer als dezentes „—" und blendet beide nativen Teile
// aus, bis die Zelle berührt oder fokussiert wird. Das Muster ist nicht neu:
// `RecordRow`/`RecordStepList` legen längst ein `opacity-0`-Date-Input über
// ein Lucide-Icon, und `index.css` lässt den Rand im Komfort-Layout erst bei
// Hover erscheinen („appears editable"). Hier ist beides in EINER Komponente
// zusammengeführt, damit Komfort- und Kompakt-Zeile nicht auseinanderlaufen.
//
// Bedienbarkeit bleibt vollständig: es ist weiterhin ein echtes, fokussier-
// bares, per `aria-label` beschriftetes `<input type="date">`. Nur die
// Anzeige des Leerzustands ist ruhiger.

import { useEffect, useRef } from "preact/hooks";
/** TT.MM.JJJJ — dasselbe Format, das das native Feld zeigt. */
function formatDateFull(iso: string): string {
  const [y, m, d] = iso.slice(0, 10).split("-");
  return y && m && d ? `${d}.${m}.${y}` : iso;
}

/** Basisklassen des Eingabefelds — Rand erscheint erst bei Hover/Fokus. */
const BASE_CLS = [
  "peer w-full rounded border border-transparent bg-transparent px-2 py-1 text-xs tabular-nums",
  "text-[color:var(--color-on-surface)] min-h-[28px] transition-all",
  "hover:border-[color:var(--color-outline-variant)] hover:bg-[color:var(--color-surface-container-lowest)]",
  "focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)]",
  "focus:border-[color:var(--color-outline-variant)] focus:bg-[color:var(--color-surface-container-lowest)]",
  // Das native Kalendersymbol ist der einzige Fremdkörper zwischen den
  // Lucide-Icons der Zeile. Es verschwindet, bis die Zelle berührt oder
  // fokussiert wird — anklickbar bleibt es die ganze Zeit (opacity entfernt
  // keine Trefferfläche), es ist also nur unsichtbar, nicht abgeschaltet.
  "[&::-webkit-calendar-picker-indicator]:opacity-0",
  "[&:hover::-webkit-calendar-picker-indicator]:opacity-60",
  "[&:focus::-webkit-calendar-picker-indicator]:opacity-100",
].join(" ");

/**
 * Zusatzklassen für den LEEREN Zustand: der Browser-Platzhalter „tt.mm.jjjj"
 * wird ausgeblendet, damit das „—" darunter sichtbar ist. Beim Fokus kommt die
 * Eingabemaske sofort zurück — sonst könnte man nicht tippen.
 *
 * Als Konstante exportiert, weil ein Test genau diese Unterdrückung prüft:
 * ohne sie ist der Platzhalter wieder da, und das lässt sich in happy-dom
 * nicht am gerenderten Text ablesen (native Shadow-Parts rendert kein
 * Test-DOM).
 */
export const DATE_EMPTY_SUPPRESS_CLS = [
  "text-transparent",
  "[&::-webkit-datetime-edit]:opacity-0",
  "[&:focus::-webkit-datetime-edit]:opacity-100",
  "focus:text-[color:var(--color-on-surface)]",
].join(" ");

export function TaskDateCell(props: {
  value: string | null;
  onChange: (next: string | null) => void | Promise<void>;
  ariaLabel: string;
  readOnly?: boolean;
}): preact.JSX.Element {
  const ref = useRef<HTMLInputElement>(null);
  // Controlled date-inputs übernehmen einen von außen (z.B. durch die
  // Start≤Ende-Regel) geänderten value nicht immer zuverlässig im DOM. Hier
  // erzwingen wir, dass die Anzeige stets dem echten Datenwert folgt.
  useEffect(() => {
    const el = ref.current;
    if (el && el.value !== (props.value ?? "")) {
      el.value = props.value ?? "";
    }
  }, [props.value]);

  if (props.readOnly) {
    return (
      <span class="block w-full px-2 py-1 text-right text-xs tabular-nums text-[color:var(--color-on-surface)] min-h-[28px]">
        {props.value ? formatDateFull(props.value) : "—"}
      </span>
    );
  }

  const empty = props.value === null || props.value === "";

  // Anzeige rechtsbuendig in Tabellenziffern (Formular-Welt: Daten stehen
  // spaltenrecht). Chrome laesst den Wert im nativen Datumsfeld nicht nach
  // rechts ausrichten — deshalb blendet die Zelle den nativen Text aus und
  // legt den formatierten Wert darueber. Beim Fokus erscheint die native
  // Eingabemaske, damit getippt werden kann; per Klick oeffnet weiterhin der
  // Datumswaehler.
  return (
    <span class="relative block w-full">
      <input
        // key auf den Wert: ändert sich der value von außen (Start≤Ende-Regel),
        // erzwingt der Remount zuverlässig die Anzeige des neuen Datums — ein
        // bloßes controlled value reicht bei date-Inputs nicht immer.
        key={props.value ?? "empty"}
        ref={ref}
        type="date"
        value={props.value ?? ""}
        onChange={(e) => {
          const v = (e.target as HTMLInputElement).value;
          void props.onChange(v || null);
        }}
        class={`${BASE_CLS} ${DATE_EMPTY_SUPPRESS_CLS}`}
        aria-label={props.ariaLabel}
      />
      {/* Liegt ÜBER dem Feld, fängt aber keine Klicks ab (pointer-events-none),
          damit der Klick weiterhin das Datumsfeld trifft. Verschwindet beim
          Fokus; bei Hover rückt er vor das Kalendersymbol. */}
      <span
        class={`pointer-events-none absolute inset-y-0 right-2 flex items-center text-xs tabular-nums peer-hover:right-7 peer-focus:opacity-0 ${
          empty
            ? "text-[color:var(--color-on-surface-variant)]"
            : "text-[color:var(--color-on-surface)]"
        }`}
        aria-hidden="true"
      >
        {empty ? "—" : formatDateFull(props.value as string)}
      </span>
    </span>
  );
}
